import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import AuthLayout from "../Components/Common/AuthLayout";
import ajaxUser from "../util/remote/ajaxUser";
import functions from "../util/functions";

/**
 * Choosing a password for the first time.
 *
 * An account is created with a temporary password, and until its owner replaces
 * it the account is marked as not yet secured. Signing in with one of those
 * lands here and goes no further: a shared starting password that nobody ever
 * changes is the same as no password at all.
 *
 * The temporary one is asked for again because it is the only proof that the
 * person at the keyboard is the one it was given to.
 */
export default function ActivateAccount() {
  const navigate = useNavigate();
  const [current, setCurrent] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const user = functions.readUser();

  const activate = async (e) => {
    e.preventDefault();
    if (!current) return toast.error("Enter the password you were given.");
    if (password.length < 6) return toast.error("Choose a password of at least 6 characters.");
    if (password !== confirm) return toast.error("The two passwords do not match.");
    if (password === current) return toast.error("Choose something different from the one you were given.");

    setBusy(true);
    const res = await ajaxUser.updatePassword({
      user_id: user?.user_id,
      old_password: current,
      new_password: password,
    });
    setBusy(false);

    if (res.status !== "OK") {
      return toast.error(res.message || "That did not work. Try again.");
    }

    // The password on the account has changed, so the session opened with the
    // old one is no longer the right thing to be carrying. Sign in again.
    toast.success("Password set. Sign in with it now.");
    setTimeout(() => functions.signOut(), 1200);
  };

  return (
    <AuthLayout
      title="Choose your password"
      subtitle={
        user?.username
          ? `Welcome, ${user.username}. Set a password of your own before you go on.`
          : "Set a password of your own before you go on."
      }
      footer={
        <button type="button" onClick={() => functions.signOut()}>
          Sign in as somebody else
        </button>
      }
    >
      <form onSubmit={activate}>
        <div className="auth-field">
          <label htmlFor="ac-current">The password you were given</label>
          <div className="auth-input-wrap">
            <input
              id="ac-current"
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              autoFocus
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="ac-new">Your new password</label>
          <div className="auth-input-wrap">
            <input
              id="ac-new"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 6 characters"
              autoComplete="new-password"
            />
          </div>
        </div>

        <div className="auth-field">
          <label htmlFor="ac-confirm">Type it again</label>
          <div className="auth-input-wrap">
            <input
              id="ac-confirm"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </div>

        <button type="submit" className="auth-submit" disabled={busy}>
          {busy ? "Saving…" : "Set my password"}
        </button>
      </form>
    </AuthLayout>
  );
}
